import * as React from "react";
import { NavLink, useMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useAppDispatch } from "utils/hooks";
import { updateEntities } from "redux-query";
import { logoutUser } from "modules/user";
import type { RootState } from "StoreTypes";
import { getCurrentUserToken } from "utils/tokens/tokenHelpers";
// Solid ri icons to match the sidebar's solid custom brand SVGs (the dominant,
// intentional style); consolidated into one library (drops react-icons/lia).
import {
  RiAppsFill,
  RiCalendarScheduleFill,
  RiAdvertisementFill,
  RiTrophyFill,
} from "react-icons/ri";
import MarketingIcon from "components/svg/MarketingIcon";
import GearIcon from "components/svg/GearIcon";
import LockerRoomIcon from "components/svg/LockerRoomIcon";
import StoreIcon from "components/svg/StoreIcon";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { NavUser } from "@/components/nav-user";
import { LeaguesNavSection } from "features/leagues-v2/components/LeaguesNavSection";
import { NavSecondary } from "./nav-secondary";
import type { IconType } from "react-icons";
import { IconHelp, IconSearch } from "@tabler/icons-react";
import { FacilitySwitcher } from "./ui/layout/FacilitySwitcher";
import { getCurrentFacility } from "modules/facility/facilitySelectors";

function NavItem({
  to,
  label,
  exact = false,
  isActive: isActiveProp,
  children,
}: {
  to: string;
  label: string;
  exact?: boolean;
  isActive?: boolean;
  children: React.ReactNode;
}) {
  const matchIsActive = !!useMatch({ path: to, end: exact });
  const isActive = isActiveProp !== undefined ? isActiveProp : matchIsActive;
  return (
    <SidebarMenuItem>
      {/* No manual active color/radius here — the base SidebarMenuButton owns
          rounded-lg + the data-active treatment (accent fill, accent-foreground
          for icon AND label). Those tokens are green in light and bright-sun in
          dark by design; hard-coding a label color broke that and left the icon
          and label mismatched in dark mode. Matches LeaguesNavSection. */}
      <SidebarMenuButton
        render={<NavLink to={to} />}
        isActive={isActive}
        tooltip={label}
      >
        {children}
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const dispatch = useAppDispatch();
  const {
    rexReservations,
    leaguesV2,
    marketingHub,
    events,
    clubhouseAdmin,
    lockerRoom,
    adKit,
    eventsNewUi,
  } = useFlags();

  const clubhouseUserClaim = useSelector(
    (state: RootState) => state.user?.clubhouseAdmin,
  );
  const canUseAdSpaces = useSelector(
    (state: RootState) => getCurrentFacility(state)?.capabilities?.canUseAdKit,
  );

  const userToken = getCurrentUserToken();
  const user = {
    name: userToken?.userName ?? "",
    email: userToken?.email ?? "",
    avatar: "",
  };

  const handleLogout = () => {
    logoutUser();
    dispatch(
      updateEntities({
        facilities: () => undefined,
        simulators: () => undefined,
      }),
    );
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* FacilitySwitcher is a complete header unit — it renders its own
          SidebarMenu > SidebarMenuItem > dropdown (logo, "Clubhouse", facility
          name, switch-facility menu). It used to be wrapped in a NavLink
          SidebarMenuButton to /dashboard, which nested interactive controls and
          made a click on the switcher also navigate. Render it directly. */}
      <SidebarHeader className="mb-2">
        <FacilitySwitcher />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu id="app-sidebar-menu">
              <NavItem to="/dashboard" label="Control" exact>
                <RiAppsFill className="size-4 shrink-0" />
              </NavItem>

              {(events || eventsNewUi) && (
                <NavItem to="/events" label="Events">
                  <RiTrophyFill className="size-4 shrink-0" />
                </NavItem>
              )}

              {rexReservations && (
                <NavItem to="/reservations" label="Reservations">
                  <RiCalendarScheduleFill className="size-4 shrink-0" />
                </NavItem>
              )}

              {/* Leagues is a Production Beta, gated on its own flag. */}
              {leaguesV2 && <LeaguesNavSection />}

              {marketingHub && (
                <NavItem to="/marketing" label="Marketing Hub">
                  <MarketingIcon class="size-4 shrink-0" />
                </NavItem>
              )}

              {lockerRoom && (
                <NavItem to="/lockerroom" label="Locker Room">
                  <LockerRoomIcon class="size-4 shrink-0" />
                </NavItem>
              )}

              {clubhouseAdmin && adKit && canUseAdSpaces && (
                <NavItem to="/ad-spaces" label="Ad Spaces">
                  <RiAdvertisementFill className="size-4 shrink-0" />
                </NavItem>
              )}

              {clubhouseAdmin && clubhouseUserClaim === true && (
                <NavItem to="/admin" label="Clubhouse Admin">
                  <GearIcon class="size-4 shrink-0" />
                </NavItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <NavSecondary
          items={[
            {
              title: "Store",
              url: "https://store.aboutgolf.com/",
              icon: StoreIcon as IconType,
            },
            {
              title: "Get Help",
              url: "https://www.aboutgolf.com/support",
              icon: IconHelp,
            },
            {
              title: "Knowledgebase",
              url: "https://aboutgolf.my.site.com/Knowledgebase/s/",
              icon: IconSearch,
            },
          ]}
          className="mt-auto"
        />
      </SidebarContent>

      <SidebarFooter>
        <NavUser user={user} onLogout={handleLogout} />
      </SidebarFooter>
    </Sidebar>
  );
}
